import apiClient from "@/services/api-client";
import type { AnalyticsOverview, RevenueReport } from "@/types";

export interface MRRTrendPoint {
  month: string;
  mrr: number;
  newMrr: number;
  churnedMrr: number;
  netNew: number;
}

export interface ChurnData {
  month: string;
  churnRate: number;
  churnedTenants: number;
  totalTenants: number;
}

export interface FeatureAdoption {
  module: string;
  adoptionRate: number;
  activeTenants: number;
  totalEligible: number;
}

export interface TenantHealth {
  id: string;
  name: string;
  healthScore: number;
  lastActive: string;
  loginFrequency: number;
  featureUsage: number;
  billingStatus: string;
  riskLevel: "low" | "medium" | "high" | "critical";
}

export interface RevenueByPlan {
  plan: string;
  revenue: number;
  tenantCount: number;
  percentage: number;
}

export const analyticsService = {
  getOverview: async () => {
    const response = await apiClient.get<AnalyticsOverview>(
      "/analytics/overview",
    );
    return response.data;
  },

  getMRRTrends: async (months: number = 12) => {
    const response = await apiClient.get<MRRTrendPoint[]>(
      "/analytics/mrr-trends",
      { params: { months } },
    );
    return response.data;
  },

  getChurnData: async (months: number = 12) => {
    const response = await apiClient.get<ChurnData[]>("/analytics/churn", {
      params: { months },
    });
    return response.data;
  },

  getFeatureAdoption: async () => {
    const response = await apiClient.get<FeatureAdoption[]>(
      "/analytics/feature-adoption",
    );
    return response.data;
  },

  getTenantHealth: async (params?: {
    sortBy?: string;
    order?: string;
    riskLevel?: string;
    limit?: number;
  }) => {
    const response = await apiClient.get<TenantHealth[]>(
      "/analytics/tenant-health",
      { params },
    );
    return response.data;
  },

  getRevenueByPlan: async () => {
    const response = await apiClient.get<RevenueByPlan[]>(
      "/analytics/revenue-by-plan",
    );
    return response.data;
  },

  getRevenueReport: async (months: number = 12) => {
    const response = await apiClient.get<RevenueReport>(
      "/analytics/revenue-report",
      { params: { months } },
    );
    return response.data;
  },
};
